// Day supplier for the two cascade "Phase 2" future searches.
//
// Those loops walk forward one day at a time, up to 90 days, and used to
// `await fetchEqpPlanner(dateStr)` on every single iteration -- 14 sequential
// round trips at the very least (measured 9.7s on dev, 1.9s on staging), even
// when nothing was found.
//
// This fetches the candidate days in concurrent chunks and hands them back in
// order, so the loop body, its filters and its early-exit stay exactly as they
// were. Safe because the candidate dates are `startDate + 0..maxDays`, fixed
// before the loop begins -- only *when to stop* depends on the data, and the
// early exit still decides that, so at most one extra chunk is ever fetched
// (and that chunk cost one round trip, not 14).
//
// Lives outside the component so the "chunked returns exactly what sequential
// returned" property can be tested with an injected fake.

/**
 * @param fetchDay  (dateString) => Promise<response>  -- injected, so tests do
 *                  not need the network. In the app this is fetchEqpPlanner.
 * @param startDate dayjs-like object supporting .add(n,'day').format(...)
 * @param maxDays   how many candidate days exist at all (the loop's own cap)
 * @param chunkSize how many days to fetch concurrently per round trip
 * @returns (dayOffset) => Promise<response|null>
 *          Resolves with the day's response, or null past `maxDays`.
 *          **Rethrows** a day's fetch error on access, so a caller's existing
 *          per-day try/catch behaves identically to the sequential version:
 *          warn, and move on without counting the day as empty.
 */
export const createDayFetcher = (fetchDay, startDate, maxDays, chunkSize = 14) => {
  const cache = new Map();
  let fetchedUpTo = 0;

  const fetchChunk = async () => {
    const from = fetchedUpTo;
    const to = Math.min(from + chunkSize, maxDays);
    const offsets = [];
    for (let offset = from; offset < to; offset += 1) offsets.push(offset);

    // Settle each day independently: one bad day must not discard the other
    // thirteen, and its error has to survive until the caller asks for it.
    const settled = await Promise.all(offsets.map(offset =>
      fetchDay(startDate.add(offset, 'day').format('YYYY-MM-DD'))
        .then(value => ({ value }))
        .catch(error => ({ error }))
    ));

    offsets.forEach((offset, i) => cache.set(offset, settled[i]));
    fetchedUpTo = to;
  };

  return async (dayOffset) => {
    if (dayOffset < 0 || dayOffset >= maxDays) return null;

    // Keep pulling chunks until this offset is covered. A `while` rather than a
    // single fetch, so an out-of-order or skipping caller still gets real data
    // instead of a silent null.
    while (!cache.has(dayOffset) && fetchedUpTo < maxDays) {
      await fetchChunk();
    }

    const entry = cache.get(dayOffset);
    if (!entry) return null;
    if (entry.error) throw entry.error;
    return entry.value;
  };
};

export default createDayFetcher;
